// Painting pins (port of web/pins.js): the shared pin look as images for the map, emoji drawn with their visible
// pixels centred. Runs in the image worker (paint.worker.ts) and, where a browser lacks OffscreenCanvas, on the page:
// nothing here touches the DOM. Colours come from setColors (the page reads them from the tokens).
import { SHARED } from "../shared/config";
import { RULES, type Look, type Shape, type Tone } from "../shared/rules";

export type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;
export function newCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas"); c.width = w; c.height = h; return c;
}
const ctx2d = (c: AnyCanvas, read = false) => c.getContext("2d", read ? { willReadFrequently: true } : undefined) as Ctx;

/** Token name ("now-fill") → a CSS colour. */
let colorOf: (name: string) => string = () => "rgb(0, 0, 0)";
export function setColors(fn: (name: string) => string) { colorOf = fn; faces.clear(); }
/** Every token the drawings use (the page sends these to the worker). */
export const PAINT_TOKENS = ["now-fill", "now", "soon-fill", "soon", "surface", "text", "selected", "on-color"] as const;
const cssColor = (name: string) => colorOf(name);

export const GROUP_MAX = RULES.groupSize("interest");
export interface Face { emojis: string[]; level: string; size: number }

// ---------- the shape ----------
// A closed outline of points around (0, 0) with half-size r: "squircle" (free) is a rounded square with continuous
// corners; "ticket" (paid or unknown price) is the same with a semicircular notch cut into the middle of each side.
// Each corner is a superellipse reaching CORNER_REACH × its radius along its edges (iOS's continuous corners).
const CORNER_REACH = 1.528, CORNER_POWER = 3.26, CORNER_STEPS = 48;
type Pt = [number, number];
export const outlines = new Map<string, Pt[]>();
export function shapePoints(shape: Shape, r: number): Pt[] {
  const key = `${shape}|${r}`;
  const hit = outlines.get(key);
  if (hit) return hit;
  let pts: Pt[] = [];
  const reach = Math.min(r, CORNER_REACH * SHARED.pins.squareCorner * 2 * r), inner = r - reach, ex = 2 / CORNER_POWER;
  for (let k = 0; k < 4; k++) {
    for (let i = 0; i <= CORNER_STEPS; i++) {
      const t = (i / CORNER_STEPS) * (Math.PI / 2), x = inner + reach * Math.cos(t) ** ex, y = inner + reach * Math.sin(t) ** ex;
      pts.push([[x, -y, -x, y][k]!, [y, x, -y, -x][k]!]);
    }
  }
  if (shape === "ticket") for (const cx of [r, -r]) pts = cutNotch(pts, cx, SHARED.pins.notchRadius * 2 * r);
  outlines.set(key, pts);
  return pts;
}
// Cuts a semicircle of radius nr centred on (cx, 0) out of an outline: the run of points inside it becomes the arc.
function cutNotch(pts: Pt[], cx: number, nr: number): Pt[] {
  const inside = ([x, y]: Pt) => Math.hypot(x - cx, y) < nr, angle = ([x, y]: Pt) => Math.atan2(y, x - cx);
  const start = pts.findIndex((p) => !inside(p)), ring = [...pts.slice(start), ...pts.slice(0, start)], out: Pt[] = [];
  for (let i = 0; i < ring.length; i++) {
    if (!inside(ring[i]!)) { out.push(ring[i]!); continue; }
    let j = i;
    while (j < ring.length && inside(ring[j]!)) j++;
    const a0 = angle(ring[i - 1]!), a1 = angle(ring[j % ring.length]!), inward = cx > 0 ? Math.PI : 0;
    let span = a1 - a0;
    const mid = (d: number) => Math.cos(a0 + d / 2 - inward); // go round the side facing the middle of the pin
    for (const alt of [span - 2 * Math.PI, span + 2 * Math.PI]) if (Math.abs(alt) < 2 * Math.PI && mid(alt) > mid(span)) span = alt;
    for (let k = 0; k <= 24; k++) out.push([cx + nr * Math.cos(a0 + (span * k) / 24), nr * Math.sin(a0 + (span * k) / 24)]);
    i = j - 1;
  }
  return out;
}
export const pathOf = (pts: Pt[]) => `M${pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join("L")}Z`;
function traceShape(ctx: Ctx, cx: number, cy: number, pts: Pt[]) {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(cx + x, cy + y) : ctx.moveTo(cx + x, cy + y)));
  ctx.closePath();
}

// The pin's fill and border by time tone: now-ish a light green fill and a green border, soon blue, else white.
export const OUTLINE: Record<Tone, { fill: string; color: string; width: number }> = {
  now: { fill: "now-fill", color: "now", width: SHARED.pins.outline.nowWidth },
  soon: { fill: "soon-fill", color: "soon", width: SHARED.pins.outline.soonWidth },
  plain: { fill: "surface", color: "surface", width: SHARED.pins.outline.plainWidth },
};
function paintShape(ctx: Ctx, mid: number, r: number, style: Look) {
  const o = OUTLINE[style.outline];
  ctx.shadowColor = "rgba(0,0,0,.22)"; ctx.shadowBlur = 8; ctx.shadowOffsetY = 2;
  traceShape(ctx, mid, mid, shapePoints(style.shape, r)); ctx.fillStyle = cssColor(o.fill); ctx.fill();
  ctx.shadowColor = "transparent";
  traceShape(ctx, mid, mid, shapePoints(style.shape, r - o.width / 2));
  ctx.lineWidth = o.width; ctx.lineJoin = "round"; ctx.strokeStyle = cssColor(o.color); ctx.stroke();
}
// ---------- emoji ----------
// Every emoji is drawn on a canvas with its drawn pixels centred: neither text layout nor measureText can be trusted
// (Chrome on a 2x screen draws small emoji ~a fifth smaller than its box, pinned left). Each emoji is drawn once
// off-screen at the same size and scale, its visible pixels found, and the offset cached.
const EMOJI_FONT = `"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
const emojiInk = new Map<string, { x: number; y: number }>();
function emojiInkCentre(emoji: string, px: number, ratio: number) {
  const key = `${emoji}|${px}|${ratio}`;
  let ink = emojiInk.get(key);
  if (!ink) {
    const pad = Math.ceil(px * 0.75), size = Math.ceil(px * 2.5), S = Math.ceil(size * ratio);
    const ctx = ctx2d(newCanvas(S, S), true);
    ctx.scale(ratio, ratio);
    ctx.font = `${px}px ${EMOJI_FONT}`;
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    ctx.fillText(emoji, pad, pad + px);
    const data = ctx.getImageData(0, 0, S, S).data;
    let x0 = S, x1 = -1, y0 = S, y1 = -1;
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      if (data[(y * S + x) * 4 + 3]! > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    ink = x1 < 0 ? { x: px / 2, y: -px * 0.35 } : { x: (x0 + x1 + 1) / 2 / ratio - pad, y: (y0 + y1 + 1) / 2 / ratio - pad - px };
    emojiInk.set(key, ink);
  }
  return ink;
}
export function fillEmoji(ctx: Ctx, emoji: string, cx: number, cy: number, px: number) {
  const ratio = Math.round(ctx.getTransform().a * 100) / 100 || 1, ink = emojiInkCentre(emoji, px, ratio);
  ctx.font = `${px}px ${EMOJI_FONT}`;
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillText(emoji, cx - ink.x, cy - ink.y);
}
export interface PinImage { width: number; height: number; data: Uint8ClampedArray }

/** A pin image (2x): its shape and the emoji, 8px of room around it for the shadow. */
export function drawPin(emoji: string, diameter: number, style: Look): PinImage {
  const ratio = 2, size = diameter + 16;
  const c = newCanvas(size * ratio, size * ratio);
  const ctx = ctx2d(c, true); // read straight back: keep it off the GPU
  ctx.scale(ratio, ratio);
  const mid = size / 2;
  paintShape(ctx, mid, diameter / 2, style);
  ctx.fillStyle = cssColor("text");
  fillEmoji(ctx, emoji, mid, mid, Math.round(diameter * 0.55));
  return { width: c.width, height: c.height, data: ctx.getImageData(0, 0, c.width, c.height).data };
}

/** A group pin in its most favoured member's look and size with a dark count badge and 1–3 emojis (group.layout).
 *  The face (shape and emojis: the slow part, emoji rasterising) is drawn once per look and kept; each count only
 *  copies it and adds its badge. */
const faces = new Map<string, AnyCanvas>();
function groupFaceCanvas(face: Face, style: Look) {
  const key = `${face.emojis.join("")}|${style.outline}|${style.shape}|${face.level}`;
  let c = faces.get(key);
  if (!c) {
    if (faces.size > 300) faces.clear();
    const ratio = 2, d = face.size, size = d + 16, k = d / GROUP_MAX;
    c = newCanvas(size * ratio, size * ratio);
    const ctx = ctx2d(c); ctx.scale(ratio, ratio);
    const mid = size / 2;
    paintShape(ctx, mid, d / 2, style);
    ctx.fillStyle = cssColor("text");
    const layout = SHARED.group.layout[Math.max(1, Math.min(face.emojis.length, SHARED.group.layout.length)) - 1]!;
    face.emojis.forEach((e, i) => fillEmoji(ctx, e, mid + layout.offsets[i]![0]! * k, mid + layout.offsets[i]![1]! * k, layout.font * k));
    faces.set(key, c);
  }
  return c;
}
export function drawGroup(face: Face, count: number, style: Look): PinImage {
  const ratio = 2, d = face.size, size = d + 16, mid = size / 2;
  const c = newCanvas(size * ratio, size * ratio);
  const ctx = ctx2d(c, true);
  ctx.drawImage(groupFaceCanvas(face, style), 0, 0);
  ctx.scale(ratio, ratio);
  const label = String(count), bw = Math.max(18, label.length * 7 + 9), bx = mid + d / 2 - bw + 4, by = mid - d / 2 - 4;
  ctx.beginPath(); ctx.roundRect(bx, by, bw, 18, 9); ctx.fillStyle = cssColor("selected"); ctx.fill();
  ctx.lineWidth = 1.5; ctx.strokeStyle = cssColor("surface"); ctx.stroke();
  ctx.font = "bold 11px -apple-system, system-ui, sans-serif"; ctx.fillStyle = cssColor("on-color");
  const m = ctx.measureText(label);
  const l = m.actualBoundingBoxLeft, r = m.actualBoundingBoxRight, a = m.actualBoundingBoxAscent, dd = m.actualBoundingBoxDescent;
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  if ([l, r, a, dd].every(Number.isFinite) && r + l > 0) ctx.fillText(label, bx + bw / 2 + (l - r) / 2, by + 9 + (a - dd) / 2);
  else { ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(label, bx + bw / 2, by + 9); }
  return { width: c.width, height: c.height, data: ctx.getImageData(0, 0, c.width, c.height).data };
}

